/** RFC 0345 S14: invite people to a project from outside its Share dropdown,
 * the first run's "Invite people" step. The invite is S4's: `sendInvite`
 * seals it, the auth-worker holds it, and the guest joins through
 * `acceptPendingInvite`. This only names the project's group and asks
 * whether this device may invite to it. */
import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import type { ProjectDoc } from '@mod/core/model'
import type { InviteLevel } from '@mod/core/sharing'
import { useCallback } from 'react'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useDocument } from '../world/repo/useDocument'
import { useSharingAccount } from './accountContext'
import type { sendInvite } from './invites'
import { useTargetMembership } from './useTargetMembership'

type SendOptions = Parameters<typeof sendInvite>[0]

/** The group an invite grants: the project's pointer when a signed ownership
 * grant backs it, else the only group that owns it. Metadata alone selects
 * nothing. */
export function inviteGroupOf(
  pointer: unknown,
  ownerGroups: readonly string[],
): string | undefined {
  const named = String(pointer ?? '')
  if (ownerGroups.includes(named)) return named
  return ownerGroups.length === 1 ? ownerGroups[0] : undefined
}

/** Invite one address to a project's group through S4. */
export async function inviteToProject(
  options: Omit<SendOptions, 'target' | 'level'> & {
    projectId: string
    groupId: string
    title: string
    level?: InviteLevel
  },
) {
  // Sharing code loads only where sharing is on.
  const { sendInvite } = await import('./invites')
  const { projectId, groupId, title, level, ...rest } = options
  return sendInvite({
    ...rest,
    level: level ?? 'edit',
    target: {
      kind: 'project',
      documentId: projectId,
      membered: { kind: 'group', id: groupId },
      title,
    },
  })
}

/**
 * Who may invite to this project from here, as a send function, or null.
 * Null unless sharing is on (`MOD_KEYHIVE`), this device's account is open
 * (enrolled, past the account gate), and its person can edit the project's
 * group: a form that could not send is never drawn.
 */
export function useProjectInviter(projectId: string) {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  const [project] = useDocument<ProjectDoc>(projectId as AnyDocumentId)
  const membership = useTargetMembership(projectId)
  const groupId = inviteGroupOf(
    project?.sharingGroupId,
    membership.ownerGroups ?? [],
  )
  const ready = !!account && !!sharing && !!project && !!groupId
  const canInvite = ready && membership.level >= 2
  const send = useCallback(
    async (email: string) => {
      if (!account || !sharing || !groupId)
        throw new Error('Sharing is not open on this device')
      return inviteToProject({
        sharing,
        client: account.client,
        account: account.account,
        world: account.opened.world as never,
        email,
        projectId,
        groupId,
        title: String(project?.title ?? 'Project'),
      })
    },
    [account, sharing, groupId, projectId, project?.title],
  )
  return canInvite ? send : null
}
